import { useEffect, useRef } from "react";
import { http as base, BACKEND, getWs } from "@/lib/http";

const API_BASE = `${BACKEND}/api/mh`;
const http = {
  get: (u, c) => base.get(`/mh${u}`, c),
  post: (u, d, c) => base.post(`/mh${u}`, d, c),
  put: (u, d, c) => base.put(`/mh${u}`, d, c),
  delete: (u, c) => base.delete(`/mh${u}`, c),
};

export const mh = {
  overview: () => http.get("/overview"),
  departments: () => http.get("/departments"),
  department: (slug) => http.get(`/departments/${slug}`),
  setAutonomy: (slug, autonomy_cap) => http.put(`/departments/${slug}/autonomy`, { autonomy_cap }),
  agents: (params) => http.get("/agents", { params }),
  agent: (slug) => http.get(`/agents/${slug}`),
  fleet: () => http.get("/fleet"),
  setAgentStatus: (slug, status) => http.put(`/agents/${slug}/status`, { status }),
  runAgent: (slug) => http.post(`/agents/${slug}/run`),
  runs: (params) => http.get("/runs", { params }),
  approvals: (params) => http.get("/approvals", { params }),
  decide: (id, decision, note = "", edited_output = null) => http.post(`/approvals/${id}/decision`, { decision, note, edited_output }),
  value: (days = 30) => http.get("/value", { params: { days } }),
  learning: (slug) => http.get(`/agents/${slug}/learning`),
  suggest: (slug) => http.post(`/agents/${slug}/suggest`),
  suggestions: (status = "pending") => http.get("/learning/suggestions", { params: { status } }),
  acceptSuggestion: (id) => http.post(`/learning/suggestions/${id}/accept`),
  dismissSuggestion: (id) => http.post(`/learning/suggestions/${id}/dismiss`),
  prompts: (slug) => http.get(`/agents/${slug}/prompts`),
  workflows: () => http.get("/workflows"),
  workflow: (id) => http.get(`/workflows/${id}`),
  createWorkflow: (d) => http.post("/workflows", d),
  fromTemplate: (department) => http.post("/workflows/from-template", { department }),
  updateWorkflow: (id, d) => http.put(`/workflows/${id}`, d),
  deleteWorkflow: (id) => http.delete(`/workflows/${id}`),
  duplicateWorkflow: (id) => http.post(`/workflows/${id}/duplicate`),
  runWorkflow: (id) => http.post(`/workflows/${id}/run`),
  workflowRuns: (workflow_id) => http.get("/workflow-runs", { params: { workflow_id } }),
  preflight: (id) => http.get(`/workflows/${id}/preflight`),
  templates: () => http.get("/workflow-templates"),
  simulator: () => http.get("/simulator"),
  setSimulator: (enabled) => http.put("/simulator", { enabled }),
};

export const STAGES = ["Foundation", "Capture", "Generate", "Orchestrate"];
export const AUTONOMY = ["Human-led", "Human-assisted", "Fully autonomous"];
export const FLOW = ["Trigger", "Retrieve", "Reason", "Act", "Explain", "Handoff"];

export const STATUS_META = {
  live: { label: "Live", color: "#D4FF3A" },
  in_dev: { label: "In dev", color: "#9A9AAE" },
  not_started: { label: "Not started", color: "#3A3A46" },
};

const listeners = new Set();
let source = null;

function subscribe(fn) {
  listeners.add(fn);
  if (!source) {
    source = new EventSource(`${API_BASE}/stream?ws=${getWs()}`, { withCredentials: true });
    source.onmessage = (evt) => {
      let ev;
      try { ev = JSON.parse(evt.data); } catch { return; }
      listeners.forEach((l) => l(ev));
    };
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && source) { source.close(); source = null; }
  };
}

export function useMhStream(onEvent) {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;
  useEffect(() => subscribe((ev) => cbRef.current(ev)), []);
}

export function upsertRun(list, run, max = 30) {
  const rest = list.filter((r) => r.id !== run.id);
  return [run, ...rest].slice(0, max);
}

export const core = {
  connections: () => base.get("/connections"),
  setModel: (d) => base.put("/connections/model", d),
  testModel: (d) => base.post("/connections/model/test", d),
  removeKey: () => base.delete("/connections/model/key"),
  setRuntime: (d) => base.put("/connections/runtime", d),
  testRuntime: () => base.post("/connections/runtime/test"),
  rotateSecret: () => base.post("/connections/runtime/rotate-secret"),
  setTool: (name, connected) => base.put("/connections/tools", { name, connected }),
  verifyTools: (agent_id) => base.post("/connections/tools/verify", { agent_id }),
  setNightly: (enabled) => base.put("/connections/tools/nightly", { enabled }),
  setLimit: (limit) => base.put("/connections/usage-limit", { limit }),
  workspaces: () => base.get("/workspaces"),
  createWorkspace: (d) => base.post("/workspaces", d),
  members: (id) => base.get(`/workspaces/${id}/members`),
  invite: (id, d) => base.post(`/workspaces/${id}/invites`, d),
  revokeInvite: (id, token) => base.delete(`/workspaces/${id}/invites/${token}`),
  removeMember: (id, uid) => base.delete(`/workspaces/${id}/members/${uid}`),
  requests: () => base.get("/workspace-requests"),
  requestWorkspace: (d) => base.post("/workspace-requests", d),
  inviteInfo: (t) => base.get(`/auth/invite/${t}`),
  forgot: (email) => base.post("/auth/forgot-password", { email }),
  reset: (token, password) => base.post("/auth/reset-password", { token, password }),
  auditQuestions: () => base.get("/audit/questions"),
  submitAudit: (d) => base.post("/audit", d),
  audit: (id) => base.get(`/audit/${id}`),
  applyAudit: (id) => base.post(`/audit/${id}/apply`),
  audits: () => base.get("/audits"),
  setMapping: (d) => base.put("/connections/runtime/mapping", d),
  pingAgent: (agent_slug, agent_id) => base.post("/connections/runtime/ping-agent", { agent_slug, agent_id }),
  runtimeEvents: () => base.get("/connections/runtime/events"),
  useStandin: () => base.post("/connections/runtime/use-standin", { base_url: BACKEND }),
  simulateActivity: (ws) => base.post(`/openclaw-sim/${ws}/simulate-activity`),
  usageReport: (month) => base.get("/reports/usage", { params: { month } }),
  createShare: (month) => base.post("/reports/usage/share", { month }),
  toggleShare: (token, enabled) => base.put(`/reports/usage/share/${token}`, { enabled }),
  sharedReport: (token) => base.get(`/reports/shared/${token}`),
};

export async function streamPlayground(slug, body, onEvent) {
  const res = await fetch(`${API_BASE}/agents/${slug}/playground`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", "X-Workspace-Id": getWs() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(typeof j.detail === "string" ? j.detail : `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => {
      const line = p.split("\n").find((l) => l.startsWith("data:"));
      if (line) { try { onEvent(JSON.parse(line.slice(5))); } catch {} }
    });
  }
}
